import { registerDiagnosticReport } from "../slices/diagnosticReportSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetch a DiagnosticReport by ID
 * - Checks Redux store first
 * - Fetches from FHIR server if missing
 * - Registers into Redux
 */
export const fetchDiagnosticReport = async (diagnosticReportId, fhirClient, store, { forceFetch = false } = {}) => {
    //console.log("fetching diagnosticReport by id");

    if (!diagnosticReportId || !fhirClient) return null;

    // Check Redux cache
    const cached = store.getState().diagnosticReports?.[diagnosticReportId];
    if (cached && !forceFetch) {
        console.log(`🧠 DiagnosticReport ${diagnosticReportId} served from cache`);
        return cached;
    }

    try {
        console.log(`🌐 Fetching DiagnosticReport/${diagnosticReportId} from FHIR`);
        const fetched = await fhirClient.request(`DiagnosticReport/${diagnosticReportId}`);

        if (fetched?.resourceType !== "DiagnosticReport") {
            console.warn("⚠️ Unexpected DiagnosticReport response:", fetched);
            return null;
        }

        store.dispatch(registerDiagnosticReport(fetched));
        return fetched;

    } catch (err) {
        console.error("❌ Failed to fetch DiagnosticReport:", err);
        const status = err?.response?.status || err?.status || 500;
        logErrorEvent({
            errorType: "read",
            error: {
                message: err?.message || "DiagnosticReport fetch failed",
                status,
                resourceId: `DiagnosticReport/${diagnosticReportId}`
            },
            description: `Failed to retrieve DiagnosticReport/${diagnosticReportId} from FHIR server`,
            store
        });
        return { error: status };
    }
};


/**
 * Fetch a DiagnosticReport given a FHIR reference string
 * e.g. "DiagnosticReport/123" or "DiagnosticReport/123/_history/4"
 */
export const fetchDiagnosticReportByReference = async (ref, fhirClient, store, options = {}) => {
    //console.log("fetching DiagnosticReport by Reference....................");

    if (!ref || typeof ref !== "string") {
        console.warn("⚠️ Invalid DiagnosticReport reference (not a string):", ref);
        return null;
    }

    if (!ref.startsWith("DiagnosticReport/")) {
        console.warn("⚠️ Reference is not a DiagnosticReport:", ref);
        return null;
    }

    // Extract ID safely (handles versioned refs)
    const parts = ref.split("/");
    const diagnosticReportId = parts[1];

    if (!diagnosticReportId || diagnosticReportId === "undefined" || diagnosticReportId === "null") {
        console.warn("⚠️ Could not extract valid DiagnosticReport ID from reference:", ref);
        return null;
    }

    return fetchDiagnosticReport(diagnosticReportId, fhirClient, store, options);
};
